import React from 'react'
import isNil from 'lodash/isNil'
import {
  Modal,
  Form,
  Select,
  Input,
  Button,
} from '@surecompanies/core_components'
import styled from 'styled-components'
import {
  PaymentCase,
  Priority,
  PRIORITY_LABELS,
  PriorityList,
} from '../Utils/types'
import { usePaymentAssignmentUserOptions } from '../hooks/usePaymentAssignmentUserOptions'
import { PaymentAssignmentRole } from '../Utils/roles'

const AssigningInfo = styled.div`
  margin-bottom: 16px;
  font-size: 14px;
  color: #3a4951;
`

const AssigningLink = styled.span`
  color: #003146;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #004d66;
  }
`

export type AssignModalType = 'assignee' | 'escalator' | 'priority'

type AssignUserHandler = (
  caseId: number,
  userId: number | null,
  notes: string
) => void | Promise<void>

type AssignPriorityHandler = (
  caseId: number,
  priority: Priority,
  notes: string
) => void | Promise<void>

interface AssignReassignModalProps {
  visible: boolean
  paymentCase: PaymentCase | null
  assignType: AssignModalType
  onClose: () => void
  submitting?: boolean
  onAssign?: AssignUserHandler
  onAssignEscalator?: AssignUserHandler
  onAssignPriority?: AssignPriorityHandler
}

const USER_FIELD_NAME = 'userId' as const

export default function AssignReassignModal({
  visible,
  paymentCase,
  assignType,
  onClose,
  submitting = false,
  onAssign,
  onAssignEscalator,
  onAssignPriority,
}: AssignReassignModalProps) {
  const [form] = Form.useForm()
  const isEscalator = assignType === 'escalator'
  const isPriority = assignType === 'priority'
  const isUserAssign = !isPriority

  const { options: userOptions, isLoading: usersLoading } =
    usePaymentAssignmentUserOptions(
      isEscalator
        ? PaymentAssignmentRole.ESCALATOR
        : PaymentAssignmentRole.MAKER,
      {
        enabled: visible && Boolean(paymentCase) && isUserAssign,
      }
    )

  const watchedUserId = Form.useWatch(USER_FIELD_NAME, form)
  const watchedPriority = Form.useWatch('priority', form)

  if (!paymentCase) return null

  const currentUserId = isEscalator
    ? (paymentCase.escalatorId ?? undefined)
    : (paymentCase.assigneeId ?? undefined)
  const currentPriority = paymentCase.priority ?? Priority.DEFAULT
  const canSubmit = isPriority
    ? !isNil(watchedPriority) && watchedPriority !== currentPriority
    : !isNil(watchedUserId) && watchedUserId !== currentUserId

  const handleSubmit = async () => {
    if (submitting) return
    try {
      const values = await form.validateFields()
      const noteValue = values.checkerNote || ''
      if (isPriority && onAssignPriority) {
        await onAssignPriority(paymentCase.id, values.priority, noteValue)
      } else {
        const userId = Number(values[USER_FIELD_NAME])
        if (isEscalator && onAssignEscalator) {
          await onAssignEscalator(paymentCase.id, userId, noteValue)
        } else if (!isEscalator && onAssign) {
          await onAssign(paymentCase.id, userId, noteValue)
        }
      }
      form.resetFields()
      onClose()
    } catch (errorInfo) {
      console.log('Assignment submit failed:', errorInfo)
    }
  }

  const handleRemove = async () => {
    if (submitting) return
    try {
      const noteValue = form.getFieldValue('checkerNote') || ''
      if (isEscalator && onAssignEscalator) {
        await onAssignEscalator(paymentCase.id, null, noteValue)
      } else if (!isEscalator && onAssign) {
        await onAssign(paymentCase.id, null, noteValue)
      }
      form.resetFields()
      onClose()
    } catch (errorInfo) {
      console.log('Remove assignment failed:', errorInfo)
    }
  }

  const handleCancel = () => {
    if (submitting) return
    form.resetFields()
    onClose()
  }

  const title = isPriority
    ? 'Assign Priority'
    : isEscalator
      ? 'Assign Escalator'
      : 'Assign/Reassign Benefit'
  const userLabel = isEscalator ? 'Assign Escalator To' : 'Assign To'
  const submitLabel = isPriority
    ? 'Update Priority'
    : isEscalator
      ? 'Assign Escalator'
      : 'Assign Benefit'
  const notesPlaceholder = isPriority
    ? 'Add any relevant notes about this priority change...'
    : isEscalator
      ? 'Add any relevant escalation notes...'
      : 'Add any relevant notes for the Maker...'

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={500}
      maskClosable={!submitting}
      keyboard={!submitting}
      destroyOnClose
    >
      <AssigningInfo>
        Payment Assignment ID: <AssigningLink>{paymentCase.id}</AssigningLink>
      </AssigningInfo>
      {paymentCase.benefitId && (
        <AssigningInfo>
          Benefit ID: <AssigningLink>{paymentCase.benefitId}</AssigningLink>
        </AssigningInfo>
      )}
      {paymentCase.primaryPaymentMethod && (
        <AssigningInfo>
          Primary Payment Method:{' '}
          <strong>{paymentCase.primaryPaymentMethod}</strong>
        </AssigningInfo>
      )}

      <Form form={form} layout="vertical" preserve={false}>
        {isUserAssign && (
          <Form.Item
            name={USER_FIELD_NAME}
            label={userLabel}
            initialValue={currentUserId}
            rules={[{ required: true, message: 'Please select a user' }]}
          >
            <Select
              placeholder="Select user"
              loading={usersLoading}
              showSearch
              optionFilterProp={'data-search'}
            >
              {userOptions.map((u) => (
                <Select.Option
                  key={u.id}
                  value={u.id}
                  data-search={`${u.name} ${u.role ?? ''}`}
                >
                  {u.name} ({u.role ?? 'NO_ROLE'})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {isPriority && (
          <Form.Item
            name="priority"
            label="Priority"
            initialValue={currentPriority}
            rules={[{ required: true, message: 'Please select a priority' }]}
          >
            <Select placeholder="Select Priority">
              {PriorityList.map((priority) => (
                <Select.Option key={priority} value={priority}>
                  {PRIORITY_LABELS[priority]}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item name="checkerNote" label="Notes">
          <Input.TextArea
            rows={4}
            placeholder={notesPlaceholder}
            style={{ resize: 'vertical' }}
          />
        </Form.Item>
      </Form>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          marginTop: '24px',
        }}
      >
        <div>
          {isUserAssign && currentUserId && (
            <Button
              danger
              onClick={handleRemove}
              loading={submitting}
              disabled={submitting}
            >
              {isEscalator ? 'Remove Escalator' : 'Remove Maker'}
            </Button>
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button onClick={handleCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={submitting}
            disabled={!canSubmit}
          >
            {submitLabel}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
